import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

const POINTER_EVENTS = 'pointer-events';

/**
 * Events that cannot be published while a native drag is in progress, so seeing any of
 * them means the drag is over. Used to undo the modal patch below if it is left behind.
 */
const INTERACTION_END_EVENTS = ['pointermove', 'pointerdown', 'drop', 'dragend'] as const;

/**
 *
 * `@atlaskit/modal-dialog` patches around a Chrome bug (crbug.com/362301053) that broke
 * dragging over cross origin iframes. While a modal is open it sets
 * `pointer-events: none !important` on every cross origin iframe on the page for the
 * duration of a drag.
 *
 * See https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/platform/packages/design-system/modal-dialog/src/internal/components/modal-dialog.tsx#lines-191
 *
 * If the above patch is removed, this hook can be deleted as well.
 *
 * The patch applies to every cross origin iframe on the page, which includes the
 * modal's own resource - so it blocks drag events from reaching it.
 *
 * This hook undoes the patch for the modal's iframe immediately, and for the app's
 * other iframes once the drag has ended.
 *
 * @returns a `ref` to attach to the iframe
 */
export function useKeepIframeInteractiveDuringDrag({
	isEnabled,
	isInModal,
}: {
	isEnabled: boolean;
	isInModal?: boolean;
}): React.RefCallback<HTMLElement> {
	const observerRef = useRef<MutationObserver | null>(null);
	const interactionEndRef = useRef<AbortController | null>(null);

	/**
	 * Unbinds everything this hook attaches: the mutation observer, and any
	 * `INTERACTION_END_EVENTS` listeners still waiting for a drag to finish -
	 * aborting the controller removes all of them in one go.
	 */
	const disconnect = useCallback(() => {
		observerRef.current?.disconnect();
		observerRef.current = null;
		interactionEndRef.current?.abort();
		interactionEndRef.current = null;
	}, []);

	// Cleanup all event listeners on unmount
	useEffect(() => {
		return () => disconnect();
	}, [disconnect]);

	return useCallback(
		(element: HTMLElement | null) => {
			// Detach from whatever was attached before: React calls this with `null` when the
			// iframe goes away, and with the new element if it is ever replaced.
			disconnect();

			if (!isEnabled || !element) {
				return;
			}

			const isBlocked = () => element.style.getPropertyValue(POINTER_EVENTS) === 'none';

			// Removed rather than overridden, so the modal's own cleanup (which
			// restores the value it recorded) stays a no-op.
			const unblock = () => element.style.removeProperty(POINTER_EVENTS);

			const restoreWhenInteractionEnds = () => {
				// already waiting for the current interaction to finish
				if (interactionEndRef.current) {
					return;
				}

				const controller = new AbortController();
				interactionEndRef.current = controller;

				const onInteractionEnd = () => {
					controller.abort();
					interactionEndRef.current = null;
					if (isBlocked()) {
						unblock();
					}
				};

				for (const type of INTERACTION_END_EVENTS) {
					window.addEventListener(type, onInteractionEnd, {
						// listening in the capture phase so a consumer stopping
						// the event cannot leave the app non-interactive
						capture: true,
						// whichever fires first removes itself, and `signal` lets
						// `disconnect` remove the rest - including on unmount
						once: true,
						signal: controller.signal,
					});
				}
			};

			const onPointerEventsChanged = () => {
				// Only the value the modal patch applies; any other inline value is
				// left as the consumer intended.
				if (!isBlocked()) {
					return;
				}

				if (isInModal) {
					// the drag has to reach the app: never leave it blocked
					unblock();
					return;
				}

				restoreWhenInteractionEnds();
			};

			// the drag may have started before this iframe mounted
			onPointerEventsChanged();

			const observer = new MutationObserver(onPointerEventsChanged);
			observer.observe(element, { attributeFilter: ['style'] });
			observerRef.current = observer;
		},
		[disconnect, isEnabled, isInModal],
	);
}
